/**
 * Week Detail Page — 6-phase wizard: Upload → Verify → Settings → Optimize → Review → Approve.
 * Core page where users manage a single optimization scenario.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import * as api from '../services/routeOptimizerService';
import WizardStepper from '../components/WizardStepper';
import DataUploadPanel from '../components/DataUploadPanel';
import VerifyPanel from '../components/VerifyPanel';
import SettingsPanel from '../components/SettingsPanel';
import OptimizationPanel from '../components/OptimizationPanel';
import ResultsPanel from '../components/ResultsPanel';
import ApprovalPanel from '../components/ApprovalPanel';
import '../styles/WeekDetail.css';

const WIZARD_STEPS = [
  { key: 'upload', label: 'Upload' },
  { key: 'verify', label: 'Verify' },
  { key: 'settings', label: 'Settings' },
  { key: 'optimize', label: 'Optimize' },
  { key: 'review', label: 'Review' },
  { key: 'approve', label: 'Approve' },
];

function deriveCompletedSteps(details) {
  const completed = [];
  if (!details) return completed;

  const hasData = (details.address_count || 0) > 0 && (details.worker_count || 0) > 0;
  if (hasData) {
    completed.push('upload');
    completed.push('verify'); // auto-pass until validation engine is wired in
  }
  // Settings always exist (defaults), so mark complete if data is uploaded
  if (hasData) completed.push('settings');
  if (details.has_results && (details.result_tiers || []).length > 0) {
    completed.push('optimize');
    completed.push('review');
  }
  return completed;
}

function deriveBestStep(details) {
  if (!details) return 'upload';
  const hasData = (details.address_count || 0) > 0 && (details.worker_count || 0) > 0;
  if (!hasData) return 'upload';
  if (details.has_results) return 'review';
  if (details.has_distance_matrix) return 'optimize';
  return 'settings';
}

export default function WeekDetail() {
  const { weekId } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState('upload');
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const initialLoadRef = useRef(true);

  const loadDetails = useCallback(async () => {
    try {
      const data = await api.getWeekDetails(weekId);
      setDetails(data);
      // Auto-navigate to the furthest completed step on initial load
      if (initialLoadRef.current) {
        setStep(deriveBestStep(data));
        initialLoadRef.current = false;
      }
    } catch (err) {
      console.error('Failed to load week details:', err);
    } finally {
      setLoading(false);
      initialLoadRef.current = false;
    }
  }, [weekId]);

  useEffect(() => { loadDetails(); }, [loadDetails]);

  const completedSteps = deriveCompletedSteps(details);

  const goToStep = (key) => setStep(key);

  const handleProceed = (nextKey) => {
    setStep(nextKey);
  };

  if (loading || !details) return (
    <div className="ro-week-detail">
      {/* Skeleton stepper */}
      <div className="ro-stepper-skeleton">
        {WIZARD_STEPS.map((s, i) => (
          <React.Fragment key={s.key}>
            {i > 0 && <div className="ro-stepper-skeleton-connector" />}
            <div className="ro-stepper-skeleton-step">
              <div className="ro-skeleton ro-skeleton-circle" />
              <div className="ro-skeleton ro-skeleton-text-sm" />
            </div>
          </React.Fragment>
        ))}
      </div>
      {/* Skeleton content */}
      <div className="ro-panel">
        <div className="ro-skeleton ro-skeleton-title" />
        <div className="ro-skeleton-row">
          <div className="ro-skeleton ro-skeleton-card" />
          <div className="ro-skeleton ro-skeleton-card ro-skeleton-delay-1" />
        </div>
        <div className="ro-skeleton ro-skeleton-block ro-skeleton-delay-2" />
      </div>
    </div>
  );

  const branchId = details.branch_id;
  const weekName = details.week_name || details.name || weekId;
  const branchName = details.branch_name;
  const companyName = details.company_name;

  return (
    <div className="ro-week-detail">
      {/* Breadcrumb */}
      <div className="ro-breadcrumb">
        <a href="/routeoptimizer">Dashboard</a>
        <span className="ro-breadcrumb-sep">{'\u203A'}</span>
        {companyName && (
          <>
            {details.company_id ? (
              <a href={`/routeoptimizer/company/${details.company_id}`}>{companyName}</a>
            ) : (
              <span>{companyName}</span>
            )}
            <span className="ro-breadcrumb-sep">{'\u203A'}</span>
          </>
        )}
        {branchId ? (
          <a href={`/routeoptimizer/branch/${branchId}`}>{branchName || 'Branch'}</a>
        ) : (
          <span>{branchName || 'Branch'}</span>
        )}
        <span className="ro-breadcrumb-sep">{'\u203A'}</span>
        <span className="ro-breadcrumb-current">{weekName}</span>
      </div>

      <WizardStepper
        steps={WIZARD_STEPS}
        currentStep={step}
        completedSteps={completedSteps}
        onStepClick={goToStep}
      />

      <div className="ro-tab-content">
        {step === 'upload' && (
          <DataUploadPanel
            weekId={weekId}
            details={details}
            onRefresh={loadDetails}
            onProceed={() => handleProceed('verify')}
          />
        )}
        {step === 'verify' && (
          <VerifyPanel
            weekId={weekId}
            details={details}
            onRefresh={loadDetails}
            onProceed={() => handleProceed('settings')}
          />
        )}
        {step === 'settings' && (
          <SettingsPanel
            entityId={weekId}
            entityType="week"
            settings={details.settings}
            onRefresh={loadDetails}
            onProceed={() => handleProceed('optimize')}
            onReset={async () => {
              const newSettings = await api.resetWeekSettings(weekId);
              loadDetails();
              return newSettings;
            }}
            resetLabel="Reset to Branch Defaults"
          />
        )}
        {step === 'optimize' && (
          <OptimizationPanel weekId={weekId} details={details} onRefresh={loadDetails} onProceed={() => handleProceed('review')} />
        )}
        {step === 'review' && (
          <ResultsPanel
            weekId={weekId}
            settings={details.settings}
            resultTiers={details.result_tiers}
            addresses={details.addresses}
            onProceed={() => handleProceed('approve')}
          />
        )}
        {step === 'approve' && (
          <ApprovalPanel weekId={weekId} details={details} />
        )}
      </div>
    </div>
  );
}
